import DadosLocais, { versaoGrupoUsuario } from '@/services/helper/storage';
import { eTipoPermissao } from '@/enums/ePermissoes';
import {
  PermissaoAcaoTelaModel,
  GrupoModel,
  PermissoesEscolaAnoPorGrupoUsuario,
} from '@/models/login/login.model';
import TextoValor from '@/models/texto-valor';
import { Constantes } from '@/services/helper/constantes';
import axios from 'axios';
import { useStore } from '@/store';
import { ParametrosModel, TipoParametrosEscola } from '@/models/administracao/escola.model';

const dadosLocais = DadosLocais.getInstance();

class SessionService {
  public MostrarAvisos(): boolean {
    const result = !dadosLocais.getExibiuMuralAvisos() && dadosLocais.getUserData() != null;
    dadosLocais.setExibiuMuralAvisos(true);
    return result;
  }

  public EhAmbienteLocalHost(): boolean {
    const isLocalHost = ['localhost'].some((env) => location.hostname.includes(env));

    return isLocalHost;
  }

  public GetEnvironment(): string {
    const isHmlEnvironment = ['localhost', 'saewebnew', 'demonstracao', 'homologacao'].some((env) =>
      location.hostname.includes(env),
    );

    return isHmlEnvironment ? 'hml' : 'prd';
  }

  public GetGrupoUsuarioAtivo(): string {
    if (this.IsAluno()) return 'ALUNO';
    else if (this.IsResponsavel()) return 'RESPONSAVEL';
    else if (this.IsProfessor()) return 'PROFESSOR';
    else return 'OUTROS';
  }

  public GetUserId(): string {
    return dadosLocais.getUserData().id.toString();
  }

  public GetLogin(): string {
    return dadosLocais.getUserData().login;
  }

  public GetValorParametroEscola(tipo: TipoParametrosEscola): any {
    const parametrosStr = dadosLocais.getEscolaSelecionadaData().parametros;

    const parametros = Object.assign(
      new ParametrosModel(),
      parametrosStr ? JSON.parse(parametrosStr) : {},
    );

    return parametros[tipo];
  }

  public IsAlunoOuResponsavelOuProfessor() {
    return this.IsAluno() || this.IsResponsavel() || this.IsProfessor();
  }

  public IsAluno(): boolean {
    return this.GetGrupoSelecionado()?.aluno === true;
  }

  public IsProfessor(): boolean {
    return this.GetGrupoSelecionado()?.professor === true;
  }

  public IsResponsavel(): boolean {
    return this.GetGrupoSelecionado()?.responsavel === true;
  }

  /** O tipo vem da ConsulteBase, nunca do nome livre do grupo. */
  public GetGrupoSelecionado(): GrupoModel | undefined {
    void versaoGrupoUsuario.value;
    const grupos = (dadosLocais.getUserData()?.grupos ?? []).filter(
      (grupo) => grupo.escolas?.length > 0,
    );
    const codigoGrupo = Number(dadosLocais.getCodigoGrupoUsuario());
    // Mesmo primeiro grupo elegível usado pelo seletor da barra no login.
    return grupos.find((grupo) => grupo.id === codigoGrupo) ?? grupos[0];
  }

  public GetCaminhoHome(): string {
    if (this.IsAluno()) return '/homealuno';
    if (this.IsResponsavel()) return '/homeresponsavel';
    if (this.IsProfessor()) return '/homeprofessor';
    return '/home';
  }

  public IsUsuarioCoordenador(): boolean {
    return (
      dadosLocais.getDescricaoGrupoUsuario()?.toUpperCase() == 'COORDENADOR' ||
      dadosLocais.getDescricaoGrupoUsuario()?.toUpperCase() == 'SUPERVISOR' ||
      false
    );
  }

  /**
   * O usuário pode VER e FALAR com a assistente virtual Sofia (permissão 2200100860).
   *
   * Vem ANTES da disponibilidade do serviço: sem a permissão, o atalho e o item do menu não
   * existem e o front nem consulta a API da IA — quem não pode usar não precisa saber se ela
   * está no ar. Mesmo desenho de `PodeLimparDiasDeAula`: vale a permissão concedida ao grupo
   * selecionado, e o suporte/desenvolvimento passa sem cadastro.
   */
  public PodeUsarSofia(): boolean {
    return (
      this.IsUsuarioSuporteOuDesenvolvimento() ||
      this.TemPermissao([eTipoPermissao.USARASSISTENTESOFIA_MENU_2200100860])[0]
    );
  }

  /**
   * O usuário pode APAGAR os dias de aula lançados (botão Deletar Aulas do Conteúdo Lecionado).
   *
   * Quem manda é a permissão 300301851 ("Permite Limpar Dias de Aula") concedida ao grupo, e não
   * o nome do grupo: `IsUsuarioCoordenador()` só reconhecia COORDENADOR e SUPERVISOR literais, de
   * modo que um grupo novo com a permissão concedida continuava sem o botão. `TemPermissao` já
   * libera o suporte, então ele segue vendo o botão sem cadastro nenhum.
   */
  public PodeLimparDiasDeAula(): boolean {
    return (
      this.IsUsuarioSuporteOuDesenvolvimento() ||
      this.TemPermissao([eTipoPermissao.PERMITELIMPARDIASDEAULA_MENU_300301851])[0]
    );
  }

  /**
   * O usuário pode VER e GRAVAR as Informações Complementares/Periódicas do estudante (a seção
   * "Informações Complementares" do cadastro do estudante e as duas opções dela no modal de
   * Relatórios).
   *
   * Permissão de AÇÃO 300100399: as Informações Complementares deixaram de ser uma janela
   * (`AlunoInformacoesComplementares.aspx`, que exigia a 300100300) e viraram uma seção do
   * cadastro, então não há rota para pôr a permissão em `meta.permissions`. Mesmo desenho de
   * `PodeLimparDiasDeAula`: vale a permissão concedida ao grupo, e o suporte passa sem cadastro.
   */
  public PodeAcessarInformacoesComplementares(): boolean {
    return (
      this.IsUsuarioSuporteOuDesenvolvimento() ||
      this.TemPermissao([eTipoPermissao.INFORMACOESCOMPLEMENTARESNOVO_MENU_300100399])[0]
    );
  }

  /**
   * O usuário pode IMPORTAR e EXPORTAR os dias de aula por planilha (botão "Importar/Exportar"
   * do Conteúdo Lecionado).
   *
   * Permissão de AÇÃO 300301852, na mesma linha da 300301851: quem abre o Conteúdo Lecionado não
   * necessariamente pode gravar conteúdo em lote nem criar dia de aula a partir de um arquivo
   * externo. Lê a permissão concedida ao grupo, nunca o nome do grupo — `TemPermissao` já libera
   * o suporte, que continua vendo o botão sem cadastro nenhum.
   */
  public PodeImportarExportarDiasDeAula(): boolean {
    return (
      this.IsUsuarioSuporteOuDesenvolvimento() ||
      this.TemPermissao([eTipoPermissao.PERMITEIMPORTAREXPORTARDIASAULA_MENU_300301852])[0]
    );
  }

  /*
   * Uma permissão de AÇÃO por botão da barra de aulas do Conteúdo Lecionado.
   *
   * As três abaixo seguem exatamente o desenho de `PodeLimparDiasDeAula`: leem a permissão
   * concedida ao GRUPO (nunca o nome dele) e o suporte passa por dentro de `TemPermissao`.
   *
   * A diferença que importa na operação: limpar e importar são ações excepcionais, e estas três
   * são o trabalho diário do professor. Como permissão não concedida é permissão inexistente, a
   * rede precisa liberá-las aos grupos antes de o deploy chegar — senão a barra de ações fica
   * vazia para todo mundo.
   */
  public PodeCopiarDiasDeAula(): boolean {
    return (
      this.IsUsuarioSuporteOuDesenvolvimento() ||
      this.TemPermissao([eTipoPermissao.PERMITECOPIARDIASAULA_MENU_300301853])[0]
    );
  }

  public PodeIncluirDiasDeAula(): boolean {
    return (
      this.IsUsuarioSuporteOuDesenvolvimento() ||
      this.TemPermissao([eTipoPermissao.PERMITEINCLUIRDIASAULA_MENU_300301854])[0]
    );
  }

  public PodeConfigurarDiasDeAula(): boolean {
    return (
      this.IsUsuarioSuporteOuDesenvolvimento() ||
      this.TemPermissao([eTipoPermissao.PERMITECONFIGURARDIASAULA_MENU_300301855])[0]
    );
  }

  /**
   * Grupos internos da Consulte, que passam por cima das travas operacionais do sistema.
   *
   * SUPORTE e DESENVOLVIMENTO valem o mesmo: os dois existem para consertar a base que travou
   * justamente por causa dessas regras. O método sempre se chamou
   * `IsUsuarioSuporteOuDesenvolvimento`, mas comparava só com 'SUPORTE' — quem estava no grupo
   * DESENVOLVIMENTO via o botão liberado por outras telas e levava recusa do servidor, que faz a
   * mesma checagem (UsuarioGrupoSqlClientRepository.UsuarioEhSuporteAsync).
   */
  public IsUsuarioSuporteOuDesenvolvimento(): boolean {
    const grupo = dadosLocais.getDescricaoGrupoUsuario()?.toUpperCase().trim();
    return grupo === 'SUPORTE' || grupo === 'DESENVOLVIMENTO';
  }

  public GetTipoEscrituracaoContrato(): number | null {
    return dadosLocais.getContractData().escrituracao;
  }

  public GetModoDiarioLancamentoEscolaSelecionada(): number {
    return dadosLocais.getEscolaSelecionadaData().diarioModoLancamento;
  }

  public GetCodigoTurmaAluno() {
    return dadosLocais.getAlunoData()?.carteirinha.codigoTurma ?? 0;
  }

  public GetCodigoAluno() {
    return dadosLocais.getAlunoData()?.carteirinha.codigoAluno ?? 0;
  }

  public GetEstudantesResponsavel(): TextoValor[] {
    const responsavelData = dadosLocais.getResponsavelData();
    if (!responsavelData) return [];

    return responsavelData.estudantes.map((x) => new TextoValor(x.codigoAluno, x.nome));
  }

  public GetCodigoUsuarioProfessor(): number {
    if (this.IsProfessor() || useStore().getters.getChamadaViaProxy)
      return useStore().getters.getCodigoProfessor;
    else return useStore().getters.getCodigoProfessorSelecionadoTelaFiltro;
  }

  // Código PESSOAL da pessoa logada (PessoalData.id, gravado no login para qualquer perfil).
  // Independe de ser professor — usado, por exemplo, para autoria de PDI em disciplina sem alocação.
  public GetCodigoPessoalLogado(): number {
    return dadosLocais.getPessoalData()?.id ?? 0;
  }

  public TemPermissao(TipoPermissao: eTipoPermissao[]): [boolean, eTipoPermissao | undefined] {
    const TelaNaoTemPermissaoConfigurada = TipoPermissao.length == 0;

    if (this.IsUsuarioSuporteOuDesenvolvimento() || TelaNaoTemPermissaoConfigurada) {
      return [true, undefined];
    } else {
      const codigoGrupoSelecionado = dadosLocais.getCodigoGrupoUsuario();

      // Obtém as permissões do grupo selecionado
      const permissoes = dadosLocais
        .getUserData()
        .grupos.find((grupo) => grupo.id.toString() === codigoGrupoSelecionado)?.permissoes;

      // Encontra o primeiro tipo de permissão que confere acesso.
      // O segMenuId é declarado como number no model, mas vem de JSON.parse e
      // pode chegar como string — e `includes` compara com igualdade estrita,
      // então '300400100' nunca casaria com 300400100. Normaliza para número.
      const permissaoEncontrada = permissoes?.find((perm) =>
        TipoPermissao.includes(Number(perm.segMenuId)),
      );

      // Verifica se há permissão
      const temPermissao = permissaoEncontrada !== undefined;

      // Retorna uma tupla com o resultado da verificação e o tipo de permissão encontrado (ou undefined)
      return [temPermissao, temPermissao ? permissaoEncontrada.segMenuId : undefined];
    }
  }

  public getNomeProfessorSelecionado() {
    return useStore().getters.getNomeProfessorSelecionadoTelaFiltro;
  }

  public exibirAvisoAcessoProfessor() {
    return this.GrupoSelecionadoTemPermissaoAcessosProfessor() && !this.IsProfessor();
  }

  public GrupoSelecionadoTemPermissaoAcessosProfessor(): boolean {
    if (this.IsUsuarioSuporteOuDesenvolvimento()) {
      return true;
    }

    const gruposExcluidosDessaPermissao = ['BIBLIOTECÁRIO'];

    const codigoGrupoSelecionado = dadosLocais.getCodigoGrupoUsuario();
    const grupoSelecionado = dadosLocais
      .getUserData()
      ?.grupos?.find((grupo) => grupo.id.toString() === codigoGrupoSelecionado);

    const permissoesValidas = [
      'planejamento_conteudo',
      'atividades_avaliativas',
      'conteudo_lecionado',
    ];

    /*
     * O `name` sozinho não basta: as telas novas ganharam permissão própria e o item do legado
     * voltou a abrir a .aspx (`name` = NULL na Seg_Menu). Quem só tem o Conteúdo Lecionado do
     * legado perdia o `name` e, com ele, a barra. Pelo número, vale a tela nova e a do legado.
     */
    const permissoesProfessor: number[] = [
      eTipoPermissao.CONTEUDOLECIONADONOVO_MENU_300301299,
      eTipoPermissao.NOMECONTEUDOLECIONADO_MENU_300301200,
      eTipoPermissao.NOMECONTEUDOLECIONADO_MENU_300301850,
      eTipoPermissao.PLANEJAMENTO_CONTEUDO_LECIONADO_MENU_300300015,
    ];

    // Se não encontrou grupo, já retorna false
    if (!grupoSelecionado) {
      return false;
    }

    // Verifica se o grupo está excluído
    const descricaoGrupoSelecionado = dadosLocais.getDescricaoGrupoUsuario();
    if (gruposExcluidosDessaPermissao.includes(descricaoGrupoSelecionado)) {
      return false;
    }

    // Verifica se tem alguma permissão válida
    return (
      grupoSelecionado.permissoes?.some(
        (perm) =>
          permissoesValidas.includes(perm.name) ||
          permissoesProfessor.includes(Number(perm.segMenuId)),
      ) ?? false
    );
  }

  public TemPermissaoEscolasAnoSemestreUsuarioAlunoResponsavelProfessor(): [
    boolean,
    PermissoesEscolaAnoPorGrupoUsuario[] | undefined,
  ] {
    //return [true, undefined]; //Para teste de sem permissões
    if (this.IsAluno() || this.IsResponsavel() || this.IsProfessor()) {
      const codigoGrupoSelecionado = parseInt(dadosLocais.getCodigoGrupoUsuario() ?? '0');
      const permissoesEscolaAnoPorGrupo = dadosLocais
        .getUserData()
        .permissoesEscolaAnoPorGrupo.filter((x) => x.codigoGrupo == codigoGrupoSelecionado);
      return [permissoesEscolaAnoPorGrupo.length > 0, permissoesEscolaAnoPorGrupo];
    } else {
      return [false, undefined];
    }
  }

  public RetornarPermissaoAcaoTela(
    TipoPermissao?: eTipoPermissao | eTipoPermissao[] | string,
  ): PermissaoAcaoTelaModel {
    const idsCandidatos = Array.isArray(TipoPermissao) ? TipoPermissao : [TipoPermissao];

    if (
      this.IsUsuarioSuporteOuDesenvolvimento() ||
      TipoPermissao === undefined ||
      TipoPermissao === '' ||
      idsCandidatos.length === 0
    ) {
      return new PermissaoAcaoTelaModel(true, true, true, true);
    }

    const codigoGrupoSelecionado = dadosLocais.getCodigoGrupoUsuario();
    const grupoSelecionado = dadosLocais
      .getUserData()
      .grupos.find((grupo) => grupo.id.toString() === codigoGrupoSelecionado);

    // Uma tela costuma ter mais de um id de permissão configurado (uma para a versão legada,
    // outra para a versão nova) — basta um deles ter o registro de permissão pra tela liberar a
    // ação. Normaliza pra número (segMenuId pode chegar como string do JSON.parse).
    const permissao = grupoSelecionado?.permissoes?.find((perm) =>
      idsCandidatos.some((id) => Number(perm.segMenuId) === Number(id)),
    );

    return new PermissaoAcaoTelaModel(
      permissao?.visualizar ?? false,
      permissao?.incluir ?? false,
      permissao?.alterar ?? false,
      permissao?.excluir ?? false,
    );
  }

  /**
   * @param motivo Item 48.1 (auditoria de sessão): por que a sessão está encerrando —
   * 'ACAO_USUARIO' (padrão, clique em "Sair") ou 'INATIVIDADE' (timeout do item 18).
   */
  public RealizarLogoff(motivo: 'ACAO_USUARIO' | 'INATIVIDADE' = 'ACAO_USUARIO') {
    if (this.TemSessaoAtiva()) {
      const headers = {
        hostname: Constantes.HEADERS_KERNEL_API,
      };

      axios.post(
        `${Constantes.URL_KERNEL_API}/Users/Logoff`,
        {
          codigoUsuario: dadosLocais.getUserData().id,
          login: dadosLocais.getUserData().login,
          usuario: dadosLocais.getUserData().nome,
          sessionId: dadosLocais.getSessionId(),
          motivo,
        },
        { headers, withCredentials: true },
      );

      dadosLocais.logout();
    }
  }

  public TemSessaoAtiva() {
    return dadosLocais.getCodContrato() ?? false;
  }
}
export default new SessionService();
